import { AIPrefillBadge } from '@components/AIPrefillBadge'
import { Input } from '@components/Input'
import {
	EMPTY_PREFILL_PROPS,
	type PrefillableFieldProps,
} from '@hooks/usePrefillableFieldProps'
import { Stack } from '@mui/material'
import {
	type ChangeEvent,
	type ComponentProps,
	type FocusEvent,
	useCallback,
} from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldError,
	type FieldPath,
	type FieldValues,
	type RegisterOptions,
} from 'react-hook-form'

// TODO(Jonathan): Add support for displayed units
type TextFieldControllerProps<TFieldValues extends FieldValues> =
	| {
			name: FieldPath<TFieldValues>
			control: Control<TFieldValues>
			label?: string
			isRequired?: boolean
			prefillProps?: PrefillableFieldProps
			onChange?: (value: number | null) => void
			type: 'number'
			unit?: string
			endAdornment?: string
			width?: 'full' | 'half'
			variant?: 'filled' | 'outlined'
			multiline?: boolean
			rows?: number
			placeholder?: string
			rules?: Omit<
				RegisterOptions<TFieldValues, FieldPath<TFieldValues>>,
				'required'
			>
			externalError?: string
	  }
	| {
			name: FieldPath<TFieldValues>
			control: Control<TFieldValues>
			label?: string
			isRequired?: boolean
			prefillProps?: PrefillableFieldProps
			onChange?: (value: string | null) => void
			type?: 'text' | 'date'
			unit?: string
			endAdornment?: string
			width?: 'full' | 'half'
			variant?: 'filled' | 'outlined'
			multiline?: boolean
			rows?: number
			placeholder?: string
			rules?: Omit<
				RegisterOptions<TFieldValues, FieldPath<TFieldValues>>,
				'required'
			>
			externalError?: string
	  }

const InputWithExtraArgOnEvents = <T,>({
	onChange,
	onBlur,
	extraArgOnEvents,
	...props
}: Omit<ComponentProps<typeof Input>, 'onChange' | 'onBlur'> & {
	extraArgOnEvents: T
	onChange?: (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		extraArg: T
	) => void
	onBlur?: (
		e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
		extraArg: T
	) => void
}) => {
	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			onChange?.(e, extraArgOnEvents),
		[onChange, extraArgOnEvents]
	)
	const handleBlur = useCallback(
		(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			onBlur?.(e, extraArgOnEvents),
		[onBlur, extraArgOnEvents]
	)
	return <Input {...props} onChange={handleChange} onBlur={handleBlur} />
}

export const TextFieldController = <TFieldValues extends FieldValues>({
	name,
	control,
	label,
	isRequired = false,
	prefillProps: { isPrefilled, onFieldChange } = EMPTY_PREFILL_PROPS,
	onChange: onChangeCallback,
	type = 'text',
	unit,
	endAdornment,
	width = 'full',
	variant = 'filled',
	multiline,
	rows,
	placeholder,
	rules,
	externalError,
}: TextFieldControllerProps<TFieldValues>) => {
	const handleChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
			onChange: (value: string | number | null) => void
		) => {
			const rawValue = e.target.value
			let newValue: string | number | null

			if (rawValue === '') {
				newValue = null
			} else if (type === 'number') {
				newValue = Number(rawValue)
			} else {
				newValue = rawValue
			}

			if (onFieldChange) {
				onFieldChange(newValue, onChange)
			} else {
				onChange(newValue)
			}

			if (onChangeCallback && type === 'text') {
				const textValue: string | null =
					typeof newValue === 'number' ? null : newValue
				;(onChangeCallback as (value: string | null) => void)(textValue)
			}
		},
		[onFieldChange, onChangeCallback, type]
	)

	const handleBlur = useCallback(
		(
			e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
			onChange: (value: string | number | null) => void
		) => {
			const value = e.target.value

			if (type === 'number' && value !== '' && value !== null) {
				const numericValue = Number(value)
				onChange(numericValue)

				if (onChangeCallback) {
					// Type assertion is safe here because we checked type === 'number'
					;(onChangeCallback as (value: number | null) => void)(numericValue)
				}
			}
		},
		[type, onChangeCallback]
	)

	const renderField = useCallback(
		({
			field: { value, onChange, ref, ...field },
			fieldState: { error },
		}: {
			field: ControllerRenderProps<TFieldValues>
			fieldState: ControllerFieldState
		}) => (
			<Stack direction="column" spacing={0.5} width="100%">
				{isPrefilled && (
					<Stack direction="row" alignItems="center" spacing={1}>
						<AIPrefillBadge />
					</Stack>
				)}
				<InputWithExtraArgOnEvents
					{...field}
					inputRef={ref}
					value={value ?? ''}
					type={type}
					label={label}
					placeholder={placeholder}
					width={width}
					variant={variant}
					unit={unit}
					endAdornment={endAdornment}
					fieldError={
						error ??
						(externalError
							? ({ message: externalError, type: 'required' } as FieldError)
							: undefined)
					}
					multiline={multiline}
					rows={rows}
					required={isRequired}
					extraArgOnEvents={onChange}
					onChange={handleChange}
					onBlur={handleBlur}
				/>
			</Stack>
		),
		[
			externalError,
			unit,
			type,
			endAdornment,
			label,
			placeholder,
			width,
			variant,
			multiline,
			rows,
			isRequired,
			isPrefilled,
			handleChange,
			handleBlur,
		]
	)

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				...(isRequired ? { required: true } : {}),
				...rules,
			}}
			render={renderField}
		/>
	)
}
